import { clone, mergeDeepRight } from 'ramda';
import { CASHIER_FIELD_TYPE } from 'js/hooks/socket/constants';
import * as ruleCreators from 'js/utils/Validation/validationRules';
import { VALIDATION_METHOD_TYPE } from 'js/utils/Validation/constants';

import { IBAN_FIELD } from '../constants';
import { IRegexpConfig, IParsedPaymentMethod, TCashierFormValidationConfig } from '../types';

export const reformatValidationConfig = (
    validationConfig: Record<string, any>
): TCashierFormValidationConfig => Object.keys(validationConfig)
    .reduce((config, name) => {
        const rules = validationConfig[name];
        const ruleTypes = Object.keys(rules);

        return {
            ...config,
            [name]: ruleTypes
                .filter(ruleType => Boolean(rules[ruleType]))
                .map((ruleType) => {
                    const options = rules[ruleType];
                    const creator = ruleCreators[ruleType as keyof typeof VALIDATION_METHOD_TYPE];

                    return creator(options);
                })
        };
    }, {});

export const getSpecificTypeRegexpConfig = ({ type, regexp, label }: {
    type: CASHIER_FIELD_TYPE; regexp?: IRegexpConfig; label: string;
}): IRegexpConfig | undefined => {
    // COR18-5978
    if (regexp && type === CASHIER_FIELD_TYPE.TEXT && label === IBAN_FIELD) {
        return {
            re: regexp.re,
            error: 'playtech.mobile.system.mobile-cashier.generic.iban.regexp'
        };
    }

    // COR18-5977
    if (type === CASHIER_FIELD_TYPE.PHONE) {
        return {
            re: regexp?.re || /^\+?[0-9-]+$/,
            error: 'playtech.mobile.system.mobile-cashier.generic.phone.regexp'
        };
    }
};

export const parseValidationConfig = (method: IParsedPaymentMethod): IParsedPaymentMethod => {
    const { amountRequired, limits, accountFields } = method;
    const validationConfig = clone(method.validationConfig) as Record<string, any>;
    const { amount } = validationConfig;

    // region Amount
    if ('amountRequired' in method) {
        amount.required = amountRequired
            ? amount.required || true // may be an object
            : false;
    }

    if (amount?.range) {
        if (limits || amount.range.min || amount.range.max) {
            if (limits) {
                amount.range = {
                    min: limits.min ?? amount.range.min,
                    max: limits.max ?? amount.range.max
                };
            }
        } else {
            // Do not use range validation without actual limits
            delete amount.range;
        }
    }

    // endregion

    // region Account fields
    accountFields?.field?.forEach(field => {
        const { name, label, isRequired, validation, type } = field;
        let validationRe = validation || '';

        validationConfig[name] = mergeDeepRight(
            { required: Boolean(isRequired) },
            validationConfig[name] || {}
        );

        if (validation) {
            let flags = '';
            const reChar = '/';

            if (validationRe[0] === reChar) {
                const lastIdx = validationRe.lastIndexOf(reChar);

                flags = validationRe.substring(lastIdx + 1);
                validationRe = validationRe.substring(1, lastIdx);
            }

            validationConfig[name] = {
                ...validationConfig[name],
                regexp: {
                    re: new RegExp(`^$|(${validationRe})`, flags),
                    error: 'playtech.mobile.system.mobile-cashier.generic.regexp-validation'
                }
            };
        }

        const specificRegexp = getSpecificTypeRegexpConfig({
            type,
            label,
            regexp: validationConfig[name].regexp
        });

        if (specificRegexp) {
            validationConfig[name] = { ...validationConfig[name], regexp: specificRegexp };
        }
    });
    // endregion

    return {
        ...method,
        validationConfig: reformatValidationConfig(validationConfig)
    };
};
